import React, { FC, ReactNode, useMemo } from 'react';

import { useIntl } from 'react-intl-next';

import {
  ForgeMergeCheckResult,
  CustomMergeChecksResourceType,
} from 'src/types/custom-merge-checks';
import { determineResourceType } from 'src/utils/custom-merge-checks';

import { MergeResultsPopupMouseCallbacks } from '../merge-checks-lists/rich-merge-checks-list/hooks';

import { messages } from './i18n';
import { MergeCheckItem } from './merge-check-item';

type CustomMergeCheckItemProps = {
  mergeCheckKey: string;
  result: ForgeMergeCheckResult;
  trailing?: ReactNode;
  // Below props are only required to show the Popup on hover.
  // The useMergeResultsPopupProps hook can provide these.
  popupIsOpen?: boolean;
  showPopup?: (key?: string) => void;
  hidePopup?: () => void;
  mouseCallbacks?: MergeResultsPopupMouseCallbacks;
};

/* eslint @typescript-eslint/ban-types: "warn" */
export const CustomMergeCheckItem: FC<CustomMergeCheckItemProps> = ({
  mergeCheckKey,
  result,
  trailing,
  popupIsOpen,
  showPopup,
  hidePopup,
  mouseCallbacks,
}) => {
  const { formatMessage } = useIntl();

  const branchName = useMemo(() => {
    const { branchConfig } = result;
    let label: string = branchConfig.identifier;
    if (branchConfig.type === 'model') {
      switch (branchConfig.identifier) {
        case 'all_branches':
          label = formatMessage(messages.allBranchesLabel);
          break;
        case 'main':
          label = formatMessage(messages.mainBranchLabel);
          break;
      }
    }
    return label;
  }, [formatMessage, result]);

  const source = useMemo(() => {
    let resourceType: CustomMergeChecksResourceType | undefined;
    try {
      resourceType = determineResourceType(result.resource);
    } catch (e) {
      resourceType = undefined;
    }

    switch (resourceType) {
      case CustomMergeChecksResourceType.Repository:
        return formatMessage(messages.repositoryCheckLabel);
      case CustomMergeChecksResourceType.Project:
        return formatMessage(messages.projectCheckLabel);
      case CustomMergeChecksResourceType.Workspace:
        return formatMessage(messages.workspaceCheckLabel);
      default:
        return undefined;
    }
  }, [result.resource, formatMessage]);

  return (
    <div data-testid="customMergeCheckItem">
      <MergeCheckItem
        mergeCheckKey={mergeCheckKey}
        status={result.status}
        name={result.check.name}
        nameAriaLabel={result.check.name}
        description={result.message}
        source={source}
        updatedOn={result.updatedOn}
        required={result.enforcement === 'required'}
        branchName={branchName}
        trailing={trailing}
        truncateName
        popupIsOpen={popupIsOpen}
        showPopup={showPopup}
        hidePopup={hidePopup}
        mouseCallbacks={mouseCallbacks}
      />
    </div>
  );
};
